/**
 * Schedule-confirmation status for the Visit Schedule Matrix (coverage map G9).
 *
 * The platform records who confirmed the Schedule of Events, when, and a
 * fingerprint of exactly what they confirmed. Comparing that fingerprint with
 * the schedule as it stands now is what turns the record into something worth
 * showing: it distinguishes "reviewed and unchanged" from "reviewed, then
 * edited" from "never reviewed".
 *
 * THIS IS A DELIBERATE PORT of `functions/src/schedule/scheduleConfirmation.ts`,
 * and the encoding below must match it byte for byte. The alternatives were a
 * new callable — more authorizing surface on a fleet just trimmed by seven — or
 * a Firestore trigger, another deployed function. Neither is worth it for a
 * read-only badge.
 *
 * The copy is not trusted, it is CHECKED. `scheduleConfirmationStatus.test.ts`
 * imports BOTH implementations and requires them to agree across a corpus, plus
 * a disagreement control. A silently drifted copy here would report CONFIRMED
 * for a schedule nobody confirmed, which is worse than showing nothing.
 *
 * ASYNC, because the browser's SHA-256 is. `crypto.subtle` needs a secure
 * context; dev and prod are both HTTPS.
 *
 * THIS MODULE MUST NOT IMPORT FROM `functions/` — the web bundle does not
 * compile that tree. Only the test reaches across, and only under the
 * functions package's own runner.
 */

/** The visit fields the fingerprint encodes. Mirrors `ScheduleVisit`. */
export interface ScheduleVisitLike {
  name?: string;
  day?: number | null;
  windowBefore?: number;
  windowAfter?: number;
  windowProvenance?: string;
  arms?: string[];
  repeats?: {
    cycleLengthDays?: number;
    until?: string;
    plannedMaxCycles?: number | null;
  };
}

/** The stored record, as the Matrix reads it out of Firestore. */
export interface ScheduleConfirmationRecordLike {
  confirmedBy?: string;
  confirmedAt?: number;
  fingerprint?: string;
  visitCount?: number;
  windowsStated?: number;
  windowsDerived?: number;
  selectedArms?: string[];
  plannedMaxCyclesByVisit?: Record<string, number>;
}

export type ConfirmationState = "CONFIRMED" | "DRIFTED" | "UNCONFIRMED";

export interface ConfirmationVerdict {
  state: ConfirmationState;
  detail: string;
}

/**
 * The separators the backend joins with, and they are NOT empty strings.
 *
 * `scheduleConfirmation.ts` writes them as RAW CONTROL CHARACTERS inside its
 * string literals — U+001F between a visit's fields and U+001E between visits.
 * `sed`, `grep` and `cat` all render them as nothing, so the source reads as
 * `join("")` however carefully you look at it; only `od -c` shows the bytes.
 * That cost a full debugging round here, and the first version of this port
 * matched the source as displayed and produced a different hash for every
 * non-empty schedule.
 *
 * They are written as explicit escapes here on purpose: a reader can see them.
 *
 * The choice itself is right and worth keeping — a separator that cannot occur
 * in the data means no two different schedules can collide by concatenation,
 * the same reasoning `signatureOf` uses with U+0000.
 */
const FIELD_SEP = String.fromCharCode(0x1F);
const ROW_SEP = String.fromCharCode(0x1E);

const toHex = (buf: ArrayBuffer): string =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

/**
 * Order-independent content fingerprint. Reordering is not a change.
 *
 * Every line of the row encoding mirrors the backend, including the details
 * that look incidental and are not: the name is trimmed and lower-cased so
 * re-typing it is not a change; `day ?? ""` keeps a null day distinct from day
 * zero; arms are sorted so their order is not content; and the window
 * PROVENANCE is included, because relabelling a window STATED -> DERIVED
 * changes what may honestly be asserted from it even when the numbers match.
 */
export async function scheduleFingerprint(visits: ScheduleVisitLike[]): Promise<string> {
  const rows = (visits ?? []).map((v) =>
    [
      String(v?.name ?? "").trim().toLowerCase(),
      v?.day ?? "",
      v?.windowBefore,
      v?.windowAfter,
      v?.windowProvenance,
      [...(v?.arms ?? [])].sort().join("+"),
      v?.repeats
        ? `${v.repeats.cycleLengthDays}/${v.repeats.until}/${v.repeats.plannedMaxCycles ?? ""}`
        : "",
    ].join(FIELD_SEP),
  );
  rows.sort();
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(rows.join(ROW_SEP)),
  );
  return toHex(digest).slice(0, 32);
}

/**
 * What the reader is told. The wording mirrors the backend's so the badge and
 * any server-side report cannot say different things about one schedule.
 */
export async function confirmationStatus(
  record: ScheduleConfirmationRecordLike | null | undefined,
  currentVisits: ScheduleVisitLike[],
): Promise<ConfirmationVerdict> {
  if (!record) {
    return { state: "UNCONFIRMED", detail: "No one has confirmed this schedule." };
  }
  const now = await scheduleFingerprint(currentVisits ?? []);
  if (now === record.fingerprint) {
    return {
      state: "CONFIRMED",
      detail: `Confirmed by ${record.confirmedBy}; ${record.visitCount} visits unchanged since.`,
    };
  }
  return {
    state: "DRIFTED",
    detail:
      `The schedule has changed since it was confirmed by ${record.confirmedBy} ` +
      `(${record.visitCount} visits approved). It must be confirmed again before ` +
      `anything is generated from it.`,
  };
}
